// Forked from api/msw/api/caseNotes/mockData.ts (Service Requests start as a copy of Cases).
import { makeNoteMockData } from '../../modules/noteMockDataFactory'
import type { IServiceRequestNote } from '~/types/note/models'

/**
 * A service request's notes. Unlike most note collections this one is SEEDED,
 * so the Service Request page's Notes card has a row to show beside the Emails
 * card.
 *
 * ⚠ Only notes. This used to hold each email's readable text too, authored by
 * Service Request Bot, as case notes did until cynque-api-server #1737; that
 * text is the email's `body` now (api/msw/api/serviceRequestEmails/mockData.ts),
 * and those five rows are gone.
 */
export async function getStartingServiceRequestNoteListData(): Promise<
  IServiceRequestNote[]
> {
  return [
    // Typed in the app by a person.
    {
      id: 'XXSRN0001',
      hrid: 'SRN0001',
      serviceRequest: {
        id: 'XXSR0001',
        label: 'POTS line at Boston dropping calls',
      },
      descr: 'Customer call: intermittent dial tone',
      note:
        'Customer called about intermittent dial tone on the Boston POTS ' +
        'line. Opened a service request and asked them to email details.',
      enteredByTeamMember: { id: 'XXTM0006', label: 'Service Technician' },
      status: { id: 1, label: 'Open' },
      createdAt: '2022-03-01T13:45:00Z',
      // ⚠ Differs from createdAt deliberately, so the Notes card shows an
      // edited note. No mock module stamps `modifiedAt` on a PATCH the way the
      // server does, so without this the edited state could not be produced by
      // clicking either.
      modifiedAt: '2022-03-04T09:12:00Z',
      version: '1',
      canBeDeleted: true,
    },
    // Created BEFORE the note above and edited AFTER it, so the Notes card's
    // newest-edited-first order differs both from the order served here and
    // from newest-created-first.
    {
      id: 'XXSRN0002',
      hrid: 'SRN0002',
      serviceRequest: {
        id: 'XXSR0001',
        label: 'POTS line at Boston dropping calls',
      },
      descr: 'Carrier ticket opened',
      note:
        'Opened a trouble ticket with the carrier for the Boston POTS line; ' +
        'updated with their reference once they replied.',
      enteredByTeamMember: { id: 'XXTM0006', label: 'Service Technician' },
      status: { id: 1, label: 'Open' },
      createdAt: '2022-02-28T15:20:00Z',
      modifiedAt: '2022-03-05T11:30:00Z',
      version: '1',
      canBeDeleted: true,
    },
  ]
}

export const {
  getNoteListData: getServiceRequestNoteListData,
  getNoteDataById: getServiceRequestNoteDataById,
  getNoteListDataByParentId: getServiceRequestNoteListDataByServiceRequestId,
} = makeNoteMockData<IServiceRequestNote>({
  entityName: 'serviceRequest',
  // Lowercase: the key is `${prefix}notes` and must equal the 'servicerequestnotes'
  // the merge handler writes (cf. 'casenotes').
  storageKeyPrefix: 'servicerequest',
  getStartingListData: getStartingServiceRequestNoteListData,
})
